import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, UserCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useBranding, buildBrandingUrl } from '../../contexts/BrandingContext';

import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { customerAuthAPI } from '../../lib/customerApi';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import ThemeToggle from '../../components/ThemeToggle';

const CustomerLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useCustomerAuth();
  const { branding } = useBranding();

  const logoSrc = buildBrandingUrl(branding?.app_logo || branding?.logo_url);


  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await customerAuthAPI.login({ email, password });
      const { accessToken, refreshToken, user } = response.data;
      login(accessToken, user);
      toast.success('Welcome!');
      navigate('/customers/chat');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="customer-login-page">
      <div className="fixed top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          {logoSrc ? (
            <img src={logoSrc} alt={branding?.brand_name || 'Logo'} className="h-12 max-w-[140px] object-contain" />
          ) : (
            <div className="w-12 h-12 rounded-md bg-primary flex items-center justify-center shadow">
              <UserCircle className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-foreground">{branding?.brand_name || 'WingsDiet'}</h1>
            <p className="text-sm text-muted-foreground">{branding?.slogan || 'Sign in to access your account'}</p>
          </div>
        </div>

        <div className="panel border border-border p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-foreground">Login</h2>
            <p className="text-muted-foreground text-sm mt-1">Use your email & password</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="customer-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  data-testid="customer-password-input"
                />
              </div>
            </div>

            <Button type="submit" className="w-full py-2.5" disabled={loading} data-testid="customer-login-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerLoginPage;
